import { uniqBy } from 'lodash';
import type {
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataUpdate,
} from '../../../common/data-retrieval/data-retrieval-interfaces';
import type { JsonData } from '../../generic-types';
import type { DropDownResult } from '../drop-down/drop-down-types';

export interface TwinColumnSelectState {
  selectedData: Array<DropDownResult>;
  sourceData?: (OdinDataRetrieval & OdinDataUpdate<JsonDataWrapper>) | JsonData;
  sourceValue: Array<DropDownResult>;
  sourceDataToRemove: Array<DropDownResult>;
  isLoading: boolean;
  textField: string;
  valueField: string;
}

export enum TwinColumnSelectActionType {
  MoveToSelected,
  GetAllDataStart,
  GetAllDataEnd,
  RemoveFromSelected,
  RemoveAllFromSelected,
  DoubleClickSource,
  DoubleClickSelected,
  AddOptionToSource,
  ResetState,
}

export interface DropDownBaseReducerAction {
  type: TwinColumnSelectActionType;
  selectedValues?: Array<DropDownResult>;
  selectedValue?: DropDownResult;
  allData?: JsonData;
  isFiltered?: boolean;
  state?: TwinColumnSelectState;
}

export default function reducer(
  state: TwinColumnSelectState,
  action: DropDownBaseReducerAction
): TwinColumnSelectState {
  // Ensure selectedData and sourceDataToRemove are always arrays for safe operations
  const selectedData = Array.isArray(state.selectedData) ? state.selectedData : [];
  const sourceDataToRemove = Array.isArray(state.sourceDataToRemove) ? state.sourceDataToRemove : [];

  switch (action.type) {
    case TwinColumnSelectActionType.MoveToSelected:
      if (action.selectedValues && action.selectedValues.length > 0) {
        const newSelections = uniqBy([...selectedData, ...action.selectedValues], 'value');
        return {
          ...state,
          selectedData: newSelections,
          sourceDataToRemove: newSelections,
        };
      }

      return { ...state };
    case TwinColumnSelectActionType.GetAllDataStart:
      return {
        ...state,
        isLoading: true,
      };
    case TwinColumnSelectActionType.GetAllDataEnd:
      if (action.allData && action.allData.length > 0) {
        const res: Array<DropDownResult> = action.allData.map((el) => {
          return {
            text: el[state.textField] as string,
            value: el[state.valueField],
          };
        });

        const finalSelections = action.isFiltered === true ? uniqBy([...selectedData, ...res], 'value') : res;

        return {
          ...state,
          selectedData: finalSelections,
          sourceDataToRemove: finalSelections,
          isLoading: false,
        };
      }

      return { ...state };
    case TwinColumnSelectActionType.RemoveFromSelected:
      if (action.selectedValues && action.selectedValues.length > 0) {
        const newSelections = selectedData.filter(
          (item) => !action.selectedValues?.some((value) => value.value === item.value)
        );

        return {
          ...state,
          selectedData: [...newSelections],
          sourceDataToRemove: [...newSelections],
        };
      }

      return { ...state };
    case TwinColumnSelectActionType.RemoveAllFromSelected:
      if (action.selectedValues && action.selectedValues.length > 0) {
        const newSelections = selectedData.filter(
          (item) => !action.selectedValues?.some((value) => value.value === item.value)
        );

        return {
          ...state,
          selectedData: [...newSelections],
          sourceDataToRemove: [...newSelections],
        };
      }

      return {
        ...state,
        sourceDataToRemove: [],
        selectedData: [],
      };
    case TwinColumnSelectActionType.DoubleClickSource:
      if (action.selectedValue) {
        return {
          ...state,
          selectedData: [...selectedData, action.selectedValue],
          sourceDataToRemove: [...sourceDataToRemove, action.selectedValue],
        };
      }
      return { ...state };
    case TwinColumnSelectActionType.DoubleClickSelected:
      if (action.selectedValue) {
        const arr = selectedData.filter((el) => el.value !== action.selectedValue?.value);
        return {
          ...state,
          selectedData: arr,
          sourceDataToRemove: arr,
        };
      }
      return { ...state };
    case TwinColumnSelectActionType.AddOptionToSource:
      if (action.selectedValue && Array.isArray(state.sourceData)) {
        const item: Record<string, unknown> = {};

        item[state.textField] = action.selectedValue.text;
        item[state.valueField] = action.selectedValue.value;

        return {
          ...state,
          sourceData: [...state.sourceData, item],
          selectedData: [...selectedData, action.selectedValue],
          sourceDataToRemove: [...selectedData, action.selectedValue],
        };
      } else if (action.selectedValue && !Array.isArray(state.sourceData)) {
        return {
          ...state,
          selectedData: [...selectedData, action.selectedValue],
          sourceDataToRemove: [...selectedData, action.selectedValue],
        };
      }
      return { ...state };
    case TwinColumnSelectActionType.ResetState:
      return {
        ...state,
        ...action.state,
      };
    default:
      return { ...state };
  }
}
